import React from "react";
import { Groups } from "@fedsim/constants";
import { getStagesRemainingCount, getStorylineTitle } from "@fedsim/storylines";
import { venues } from "@fedsim/constants";
import { differenceInDays, numberFormatCurrency } from "@fedsim/helpers";
import { Stack, Box } from "@mui/material";
import LocationOnIcon from '@mui/icons-material/LocationOn';
import {
  Completion,
  DateDisplay,
  Duration,
  SegmentCost,
  SegmentType,
  Caption,
  SPACING,
  CostRow,
  SpacedDivider
} from "../../typography";
import { NameBarStack, NameBar, AvatarStack } from "../../namebar";
import { Chip } from "../../chip";
import { Ratings } from "../../ratings.js";

import NumbersIcon from "@mui/icons-material/Numbers";
import FilterListIcon from "@mui/icons-material/FilterList";

export function FeudStack(props) {
  const {
    brands = [],
    startDate = null,
    complete = false,
    championships = [],
    wrestlers = [],
    intensity = 0,
    stageId = null,
    storylineId = null
  } = props;
  const names = wrestlers.map((item) => item.name);

  return (
    <>
      {names && (
        <Stack direction={{ xs: "column", lg: "row" }} spacing={SPACING}>
          <Stack direction="row" width="100%">
            {names.map((name, index) => (
              <React.Fragment key={name}>
                <b>{name}</b>
                {names.length - 1 !== index ? <i>&nbsp; VS &nbsp;</i> : null}
              </React.Fragment>
            ))}
          </Stack>
        </Stack>
      )}
      <div>
        Stage {stageId} of {getStagesRemainingCount({ stageId, storylineId })}
      </div>
      <div>{getStorylineTitle({ stageId, storylineId })}</div>
        <Stack spacing={1} direction="row">
          <DateDisplay prefix="Start">{startDate}</DateDisplay>
          <Completion value={complete} />
        </Stack>
      <SpacedDivider size="small" />
      <AvatarStack collection={brands.concat(championships)} />
        <Ratings value={intensity} />
    </>
  );
}

export function FactionStack(props) {
  const {
    wrestlers = [],
    managers = [],
    leaders = [],
    brands = [],
    startDate = new Date(),
    endDate = false
  } = props;
  return (
    <>
      <Stack direction="row">
        {startDate && <DateDisplay prefix="From">{startDate}</DateDisplay>}
        {endDate && <DateDisplay prefix="End">{endDate}</DateDisplay>}
      </Stack>
      <AvatarStack collection={leaders.concat(wrestlers).concat(managers)} />
      {brands && <AvatarStack collection={brands} />}
    </>
  );
}
export function BrandStack({ director, balance }) {
  return (
    <>
      {director && (
        <AvatarStack collection={[director]} />
      )}
      {balance && <CostRow label="Balance"
        value={balance} />}
    </>
  );
}

export function DraftStack(props) {
  const {
    cost,
    date = null,
    complete = null,
    brand = { name: "Vacant", image: false },
    wrestler = { name: "Vacant", image: false }
  } = props;
  return (
    <>
      <NameBar {...wrestler} />
      <NameBar {...brand} />
      {date && <DateDisplay>{date}</DateDisplay>}
      <CostRow label="Cost" value={cost} />
      <Completion value={complete} />
    </>
  );
}

export function ReignStack(props) {
  const { wrestlers, championship } = props;
  const startDate = new Date(props?.startDate);
  const endDate = props?.endDate ? new Date(props?.endDate) : false;
  const lengthOfReign = endDate ? differenceInDays(endDate, startDate) : false;
  const labelLength = !endDate ? "Ongoing" : `${lengthOfReign} days`;

  return (
    <>
      <AvatarStack collection={wrestlers.concat([championship])} />
      <DateDisplay prefix="Start">{startDate}</DateDisplay>
      {endDate && <DateDisplay prefix="End">{endDate}</DateDisplay>}
      <Chip avatar={<NumbersIcon />} label={labelLength} />
    </>
  );
}

export function ProductionStack(props) {
  const {
    brands = [],
    segmentIds = [],
    venueId = 'DEFAULT',
    cost = false,
    date = new Date(),
    complete = false
  } = props;
  const venue = venues?.find((item) => item.id === venueId);
  return (
    <>
      <AvatarStack collection={brands} />
      <Chip label={`${segmentIds?.length} segments`} avatar={<NumbersIcon />} />
      {cost && <CostRow label="Cost" value={cost} />}
      <DateDisplay>{date}</DateDisplay>
      {venue && <Chip label={venue.name} avatar={<LocationOnIcon />} />}
      <Completion value={complete} />
    </>
  );
}

export function SegmentStack(props) {
  const {
    date = null,
    duration = 0,
    complete = false,
    type = "",
    championships = []
  } = props;
  return (
    <>
      <NameBarStack collection={championships} />
      <SegmentCost>{type}</SegmentCost>
      <SegmentType>{type}</SegmentType>
      <Duration value={duration} />
      <DateDisplay>{date}</DateDisplay>
      <Completion value={complete} />
    </>
  );
}

export function AppearancesStack(props) {
  const { appearances = [] } = props;
  const collection = appearances.map((item) => item?.wrestler);

  return (
    <>
      <AvatarStack collection={collection} />
    </>
  );
}

export function ChampionshipStack({
  holders = 1,
  minPoints = null,
  maxPoints = null,
  wrestlers = [],
  brands = []
}) {
  const holderSt =
    wrestlers.length !== holders
      ? `${wrestlers.length} / ${holders} holders`
      : null;
  const pointsLabel = `${minPoints} to ${maxPoints} points`;
  return (
    <>
      <AvatarStack collection={brands.concat(wrestlers)} />
      {holderSt && <Chip avatar={<FilterListIcon />} label={holderSt} />}
      <Chip label={pointsLabel} avatar={<NumbersIcon />} />
    </>
  );
}

export function WrestlerStack({
  brands = [],
  alignment = "NEUTRAL",
  cost = null,
  color = "",
  wins = 0,
  losses = 0,
  points = 50,
  // draws = 0,
  streak = 0
}) {
  const alignmentLabel = alignment === "GOOD" ? "Goodie" : alignment === "BAD" ? "Baddie" : false
  return (
    <>
      {brands && <AvatarStack collection={brands} />}
      {alignmentLabel && (
        <Box sx={{ textAlign: "center" }}>
          {alignmentLabel} - {numberFormatCurrency(cost)}
        </Box>
      )}
      <Caption sx={{ color, textAlign: "center" }}>
        {wins}W · {losses}L · {points}pts
        {streak > 0 && (
          <span>
            &nbsp;· {streak} streak
          </span>
        )}
      </Caption>
    </>
  );
}

export const Stacks = {
  [Groups.Brand]: BrandStack,
  [Groups.Championship]: ChampionshipStack,
  [Groups.Draft]: DraftStack,
  [Groups.Faction]: FactionStack,
  [Groups.Feud]: FeudStack,
  [Groups.Production]: ProductionStack,
  [Groups.Reign]: ReignStack,
  [Groups.Segment]: SegmentStack,
  [Groups.Wrestler]: WrestlerStack
};

export default Stacks;
